/**
 * Role Utilities
 * Provides functions to check user roles and permissions
 */

import {
  decodeToken,
  getValidAuthToken,
  isTokenExpired,
} from './api/auth.js';
import { getUserData } from './api/auth.js';

const PLATFORM_ADMIN_ROLES = new Set(['super_admin', 'manager', 'staff']);
const COMPANY_ROLES = new Set(['company', 'company_staff']);

/**
 * Resolve user role from stored auth or login redirect query params.
 * @param {string} [search] - location.search (used during auth handoff before URL is cleaned)
 * @returns {string|null} User role (super_admin, manager, staff, company, company_staff, etc.)
 */
export const resolveUserRole = (search = '') => {
  try {
    if (search) {
      const params = new URLSearchParams(search);
      const tokenFromUrl = params.get('token');
      if (tokenFromUrl && !isTokenExpired(tokenFromUrl)) {
        const decoded = decodeToken(tokenFromUrl);
        if (decoded?.role) return decoded.role;
      }
      const roleFromUrl = params.get('role');
      if (roleFromUrl) return roleFromUrl;
    }

    const token = getValidAuthToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded?.role) return decoded.role;
    }

    const userData = getUserData();
    if (userData?.role) return userData.role;
    if (userData?.company || userData?.jobscribe_id) return 'company';

    return null;
  } catch (error) {
    console.warn('Failed to resolve user role:', error);
    return null;
  }
};

/**
 * Get raw user role from token or user data
 * @returns {string|null} User role (admin, super_admin, company, company_staff, etc.)
 */
export const getUserRole = () => {
  if (typeof window !== 'undefined') {
    return resolveUserRole(window.location.search);
  }
  return resolveUserRole('');
};

const roleIsPlatformAdmin = (role) => PLATFORM_ADMIN_ROLES.has(role);
const roleIsCompanyUser = (role) => COMPANY_ROLES.has(role);

/**
 * Check if user is a platform administrator/staff
 * @returns {boolean} True if user is super_admin, manager, or staff
 */
export const isPlatformAdmin = () => roleIsPlatformAdmin(getUserRole());

/**
 * Check if user is a company user
 * @returns {boolean} True if user is company or company_staff
 */
export const isCompanyUser = () => roleIsCompanyUser(getUserRole());

/**
 * Check if user is a company staff member
 * @returns {boolean} True if user is company_staff
 */
export const isCompanyStaff = () => {
  const role = getUserRole();
  return role === 'company_staff';
};

/**
 * Check if user is a company account (not staff)
 * @returns {boolean} True if user is company
 */
export const isCompanyAccount = () => {
  const role = getUserRole();
  return role === 'company';
};

/**
 * Get user type for navigation purposes
 * @returns {'admin'|'company'|'company_staff'|null} User type
 */
export const getUserType = () => {
  const role = getUserRole();
  
  if (role === 'super_admin' || role === 'manager' || role === 'staff') {
    return 'admin';
  }
  
  if (role === 'company') {
    return 'company';
  }
  
  if (role === 'company_staff') {
    return 'company_staff';
  }
  
  return null;
};

/**
 * Check if user should see admin navigation
 * @returns {boolean} True if user should see admin section
 */
export const shouldShowAdminNav = () => {
  return isPlatformAdmin();
};

/**
 * Check if user should see company navigation
 * @returns {boolean} True if user should see company section
 */
export const shouldShowCompanyNav = () => {
  return isCompanyUser();
};
